import { ReactElement, useEffect, useRef, useState } from "react";
import styles from "./Dropdown.module.css";

interface DropdownProps {
  dropdownName: string;
  content: ReactElement | string;
  children: ReactElement | string;
  className?: string;
  contentClassName?: string;
  open?: boolean;
  onOpenChange?: (newOpen: boolean) => void;
  onClickOutside?: () => void;
}

export default function Dropdown({
  dropdownName,
  content,
  children,
  className,
  contentClassName,
  open = false,
  onOpenChange,
  onClickOutside,
}: DropdownProps): ReactElement {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, toggleDropdownOpen] = useState(open);
  const [preDropdownOpen, togglePreDropdownOpen] = useState(open);
  const linksId = `dropdown-${dropdownName.replace(/\s/g, "")}`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current?.parentElement &&
        !dropdownRef.current.parentElement.contains(event.target as Node)
      ) {
        onClickOutside && onClickOutside();
      }
    };

    if (open) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [onClickOutside, open]);

  if (open != preDropdownOpen) {
    toggleDropdownOpen(open);
    togglePreDropdownOpen(open);
  }

  const handleOnClick = () => {
    onOpenChange && onOpenChange(!dropdownOpen);
    toggleDropdownOpen(!dropdownOpen);
  };

  return (
    <div className={className} data-testid={dropdownName} ref={dropdownRef}>
      <div
        aria-label={
          !dropdownOpen ? `Expand ${dropdownName}` : `Close ${dropdownName}`
        }
        aria-expanded={dropdownOpen}
        aria-controls={linksId}
        onClick={handleOnClick}
      >
        {children}
      </div>
      {dropdownOpen && (
        <div
          id={linksId}
          className={`${styles.contentContainer} ${
            contentClassName
              ? contentClassName
              : styles.defaultContentContainerExtras
          }`}
          data-testid={linksId}
        >
          {content}
        </div>
      )}
    </div>
  );
}
